import { FC } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import {
  CurriculumFrameworkImport,
  FrameworkReviewResult,
} from "services/aiRequirementsImport";

type Props = {
  framework: CurriculumFrameworkImport;
  loading: boolean;
  reviewLoading: boolean;
  reviewError: string | null;
  aiReview: FrameworkReviewResult | null;
  importBlocked: boolean;
  blockingMessages: string[];
  loadingProgress: { current: number; total: number };
  onBack: () => void;
  onSubmit: () => void;
  onAiReview: () => void;
  onApplyAiSuggestions: () => void;
};

function sourceLabel(source: CurriculumFrameworkImport["source"]): string {
  if (source === "cbve_parser") {
    return "Default Template parser";
  }
  if (source === "claude_fallback") {
    return "Claude (count mismatch fallback)";
  }
  return "Claude";
}

export const FrameworkReviewStep: FC<Props> = ({
  framework,
  loading,
  reviewLoading,
  reviewError,
  aiReview,
  importBlocked,
  blockingMessages,
  loadingProgress,
  onBack,
  onSubmit,
  onAiReview,
  onApplyAiSuggestions,
}) => {
  const progress =
    loadingProgress.total > 0
      ? Math.round((loadingProgress.current / loadingProgress.total) * 100)
      : 0;
  const busy = loading || reviewLoading;

  return (
    <StepLayoutContainer
      fullPageScroll
      title="Review framework import"
      description="Domains, competencies (with milestone eval metrics), and EPAs will be created. Competency ↔ EPA and domain links are applied automatically."
      onClickBack={busy ? undefined : onBack}
      backButtonText="Back"
      renderActionButtons={
        <Stack direction="row" spacing={2} width="100%">
          <Button
            variant="outlined"
            color="primary"
            onClick={onAiReview}
            disabled={busy}
            fullWidth
            size="small"
            startIcon={
              reviewLoading ? <CircularProgress size={16} /> : undefined
            }
          >
            {reviewLoading ? "Reviewing with AI…" : "Review with AI agent"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={busy || importBlocked}
            fullWidth
            size="small"
          >
            {loading ? "Importing…" : "Import framework"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Source: {sourceLabel(framework.source)}
            {framework.expected_counts
              ? ` · Framework expects ${framework.expected_counts.domains} domains / ${framework.expected_counts.competencies} competencies`
              : ""}
          </Typography>
          {framework.fallback_reason && (
            <Typography variant="body2" color="text.secondary">
              Fallback: {framework.fallback_reason}
            </Typography>
          )}
        </Box>

        {importBlocked && (
          <Alert severity="error">
            Import is blocked until count / AI review errors are resolved.
            {blockingMessages.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {blockingMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {reviewError && <Alert severity="error">{reviewError}</Alert>}

        {reviewLoading && (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            <Typography variant="body2">
              AI agent is comparing the spreadsheet with the extracted
              framework. This can take up to a minute.
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {aiReview && !reviewLoading && (
          <Alert severity={aiReview.ok ? "success" : "warning"}>
            <Typography variant="subtitle2" gutterBottom>
              AI review
            </Typography>
            <Typography variant="body2" gutterBottom>
              {aiReview.summary}
            </Typography>
            {aiReview.issues.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {aiReview.issues.map((issue) => (
                  <li key={`${issue.code}-${issue.message}`}>
                    [{issue.severity}] {issue.message}
                    {issue.path ? ` (${issue.path})` : ""}
                  </li>
                ))}
              </Box>
            )}
            {aiReview.suggested_framework && (
              <Button
                sx={{ mt: 1 }}
                size="small"
                variant="contained"
                onClick={onApplyAiSuggestions}
                disabled={busy}
              >
                Apply AI suggestions
              </Button>
            )}
          </Alert>
        )}

        {framework.domains.length > 0 && (
          <Box>
            <Typography variant="subtitle1">
              Domains ({framework.domains.length})
            </Typography>
            {framework.domains.map((domain) => (
              <Typography key={domain.code} variant="body2">
                {domain.code}. {domain.name}
              </Typography>
            ))}
          </Box>
        )}
        {framework.competencies.length > 0 && (
          <Box>
            <Typography variant="subtitle1">
              Competencies ({framework.competencies.length})
            </Typography>
            {framework.competencies.map((row) => (
              <Typography key={row.code} variant="body2">
                {row.name}
              </Typography>
            ))}
          </Box>
        )}
        {framework.milestones.length > 0 && (
          <Box>
            <Typography variant="subtitle1">
              Milestones ({framework.milestones.length})
            </Typography>
            {framework.milestones.map((row) => (
              <Typography
                key={`${row.skill_level}-${row.name}`}
                variant="body2"
              >
                {row.skill_level}. {row.name}
              </Typography>
            ))}
          </Box>
        )}
        {framework.epas.length > 0 && (
          <Box>
            <Typography variant="subtitle1">
              EPAs ({framework.epas.length})
            </Typography>
            {framework.epas.map((row) => (
              <Typography key={row.code} variant="body2">
                {row.name}
              </Typography>
            ))}
          </Box>
        )}

      </Stack>

      {loading && (
        <Box mt={3}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              {loadingProgress.current} / {loadingProgress.total}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}
    </StepLayoutContainer>
  );
};
